import { useModalDismiss } from '../../hooks/useModalDismiss';
import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faFile, faVideo, faHeadphones, faLink, faVideoCamera } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

// Start with smaller chunks to avoid 413 errors - will be adjusted based on server limits
const CHUNK_SIZE = 512 * 1024; // 512KB chunks - FormData adds overhead, so smaller is safer

const ResourceUpload = ({ formationId, resourceType, onClose, onSuccess }) => {
  // Escape only: this overlay has never closed on a backdrop click.
  useModalDismiss(onClose, { backdropClose: false });
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [isMeeting, setIsMeeting] = useState(false);
  const [meetingAt, setMeetingAt] = useState('');
  const [durationSeconds, setDurationSeconds] = useState('');
  const [required, setRequired] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!title) {
        setTitle(selectedFile.name);
      }
    }
  };

  const handleUpload = async () => {
    if (resourceType === 'link') {
      if (!externalUrl) {
        setError('Veuillez entrer une URL');
        return;
      }
      if (isMeeting && !meetingAt) {
        setError("Veuillez indiquer la date et l'heure de la réunion");
        return;
      }
      await handleLinkUpload();
    } else {
      if (!file) {
        setError('Veuillez sélectionner un fichier');
        return;
      }
      await handleFileUpload();
    }
  };

  const handleLinkUpload = async () => {
    setLoading(true);
    setError('');

    try {
      await api.post(`/formations/${formationId}/resources`, {
        type: 'link',
        title: title || 'Lien externe',
        description,
        external_url: externalUrl,
        is_meeting: isMeeting,
        meeting_at: isMeeting ? meetingAt : null,
        required,
      });

      onSuccess();
    } catch (error) {
      console.error('Error uploading link:', error);
      setError(error.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Check server limits and adjust chunk size dynamically
      let actualChunkSize = CHUNK_SIZE;
      try {
        const limitsResponse = await api.get('/diagnostics/upload-limits');
        console.log('Server upload limits:', limitsResponse.data);
        const postMaxSize = limitsResponse.data.data?.post_max_size_bytes;
        const recommendedChunk = limitsResponse.data.data?.recommendations?.max_chunk_size;
        
        if (postMaxSize) {
          // Use 70% of post_max_size to account for FormData overhead (headers, boundaries, etc.)
          const safeChunkSize = Math.floor(postMaxSize * 0.7);
          if (safeChunkSize < CHUNK_SIZE) {
            actualChunkSize = Math.min(safeChunkSize, 512 * 1024); // Max 512KB if limit is very low
            console.warn(`Server post_max_size is ${postMaxSize} bytes. Reducing chunk size to ${actualChunkSize} bytes for safety.`);
          }
        } else if (recommendedChunk && recommendedChunk < CHUNK_SIZE) {
          actualChunkSize = recommendedChunk;
          console.warn(`Using recommended chunk size: ${actualChunkSize} bytes`);
        }
      } catch (e) {
        // Ignore diagnostic errors, continue with default chunk size
        console.log('Could not fetch server limits:', e);
      }

      // Step 1: Initiate upload
      const initiateResponse = await api.post(`/formations/${formationId}/resources/upload/initiate`, {
        file_name: file.name,
        file_size: file.size,
        mime: file.type,
        chunk_size: actualChunkSize, // Use dynamically adjusted chunk size
      }, {
        timeout: 30000, // 30 seconds for initiate
      });

      console.log('Initiate upload response:', initiateResponse.data);

      // Handle both response formats
      const responseData = initiateResponse.data.data || initiateResponse.data;
      if (!responseData || !responseData.upload_id) {
        throw new Error('Invalid response from server: missing upload_id');
      }

      const { upload_id, total_chunks } = responseData;

      // Step 2: Upload chunks using FormData (binary, not base64)
      const chunks = Math.ceil(file.size / actualChunkSize);
      const startTime = Date.now();
      const MAX_UPLOAD_TIME = 30 * 60 * 1000; // 30 minutes max

      for (let i = 0; i < chunks; i++) {
        // Check if upload is taking too long
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime > MAX_UPLOAD_TIME) {
          throw new Error('TIMEOUT');
        }

        const start = i * actualChunkSize;
        const end = Math.min(start + actualChunkSize, file.size);
        const chunk = file.slice(start, end);

        // Use FormData for binary upload (no base64 encoding overhead)
        const formData = new FormData();
        formData.append('chunk_number', i);
        formData.append('chunk', chunk); // Binary blob, not base64

        try {
          // Use axios with FormData (Content-Type will be set automatically by browser with boundary)
          await api.post(`/formations/${formationId}/resources/upload/${upload_id}/chunk`, formData, {
            timeout: 120000, // 2 minutes per chunk
          });
        } catch (chunkError) {
          // If chunk upload fails, throw with chunk info for better error handling
          throw { ...chunkError, chunkNumber: i, totalChunks: chunks };
        }

        setUploadProgress(Math.round(((i + 1) / chunks) * 100));

        // Small delay between chunks to avoid rate limiting (except for last chunk)
        if (i < chunks - 1) {
          await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
        }
      }

      // Step 3: Finalize upload
      await api.post(`/formations/${formationId}/resources/upload/${upload_id}/finalize`, {
        type: resourceType,
        title: title || file.name,
        description,
        duration_seconds: durationSeconds ? parseInt(durationSeconds) : null,
        required,
      }, {
        timeout: 120000, // 2 minutes for finalize (file assembly)
      });

      onSuccess();
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Provide more helpful error messages in French
      if (error.message === 'TIMEOUT') {
        setError('L\'upload a pris trop de temps. Le fichier est peut-être trop volumineux ou votre connexion est trop lente. Veuillez réessayer avec un fichier plus petit.');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setError('L\'upload a pris trop de temps. Vérifiez votre connexion internet et réessayez.');
      } else if (error.response?.status === 413) {
        setError('Le fichier est trop volumineux. Veuillez utiliser un fichier plus petit.');
      } else if (error.response?.status === 429) {
        setError('Trop de tentatives. Veuillez patienter quelques instants avant de réessayer ou utiliser un fichier plus petit.');
      } else if (error.response?.status === 419) {
        setError('Session expirée. Veuillez rafraîchir la page et réessayer.');
      } else if (error.response?.status === 408 || error.response?.status === 504) {
        setError('Le serveur a mis trop de temps à répondre. L\'upload a peut-être pris trop de temps. Veuillez réessayer.');
      } else if (error.response?.status >= 500) {
        setError('Erreur serveur. Veuillez réessayer dans quelques instants. Si le problème persiste, contactez votre administrateur.');
      } else if (error.chunkNumber !== undefined) {
        // Chunk-specific error
        const chunkInfo = `(Chunk ${error.chunkNumber + 1}/${error.totalChunks})`;
        setError(`Erreur lors de l'upload du chunk ${chunkInfo}. ${error.response?.data?.message || 'Vérifiez votre connexion et réessayez.'}`);
      } else if (!error.response) {
        // Network error
        setError('Erreur de connexion. Vérifiez votre connexion internet et réessayez.');
      } else {
        setError(error.response?.data?.message || error.message || 'Erreur lors de l\'upload. Vérifiez votre connexion et réessayez.');
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:type;base64, prefix
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const getResourceIcon = () => {
    switch (resourceType) {
      case 'video': return faVideo;
      case 'audio': return faHeadphones;
      case 'link': return faLink;
      default: return faFile;
    }
  };

  const getResourceLabel = () => {
    switch (resourceType) {
      case 'video': return 'Vidéo';
      case 'audio': return 'Audio';
      case 'link': return 'Lien';
      default: return 'Fichier';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <FontAwesomeIcon icon={getResourceIcon()} className="mr-2 text-red-600" />
            Ajouter une {getResourceLabel()}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              placeholder="Titre de la ressource"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              placeholder="Description (optionnel)"
            />
          </div>

          {/* File Upload or External URL */}
          {resourceType === 'link' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL <span className="text-red-600">*</span>
              </label>
              <input
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                placeholder="https://example.com"
                required
              />

              {/* Online meeting (Teams / Zoom / Meet) */}
              <div className="mt-4 flex items-center">
                <input
                  type="checkbox"
                  id="is_meeting"
                  checked={isMeeting}
                  onChange={(e) => {
                    setIsMeeting(e.target.checked);
                    if (!e.target.checked) setMeetingAt('');
                  }}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="is_meeting" className="ml-2 block text-sm text-gray-700">
                  <FontAwesomeIcon icon={faVideoCamera} className="mr-1 text-red-600" />
                  Réunion en ligne (Teams, Zoom, Google Meet...)
                </label>
              </div>

              {isMeeting && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date et heure de la réunion <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={meetingAt}
                    onChange={(e) => setMeetingAt(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Les participants verront cette date et heure à côté du lien.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fichier <span className="text-red-600">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept={
                    resourceType === 'video' ? 'video/*' :
                    resourceType === 'audio' ? 'audio/*' :
                    '*'
                  }
                  className="hidden"
                />
                {file ? (
                  <div>
                    <p className="text-sm text-gray-700 mb-2">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-2 text-sm text-blue-600 hover:underline"
                    >
                      Changer de fichier
                    </button>
                  </div>
                ) : (
                  <div>
                    <FontAwesomeIcon icon={faUpload} className="text-4xl text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2">
                      Cliquez pour sélectionner un fichier
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      Sélectionner un fichier
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Required */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="required"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="required" className="ml-2 block text-sm text-gray-700">
              Ressource requise pour la complétion
            </label>
          </div>

          {/* Upload Progress */}
          {loading && resourceType !== 'link' && (
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Upload en cours...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-4 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleUpload}
            disabled={loading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Upload...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Uploader
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResourceUpload;

